import React from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    Outlet
} from "react-router-dom";
import Home from './pages/Home';
import BlogList from './pages/blog/BlogList';
import BlogPost from './pages/blog/BlogPost';
import NotFound from './pages/NotFound';
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminBlogPosts from "./pages/admin/blog/AdminBlogPosts";
import AdminBlogPost from "./pages/admin/blog/AdminBlogPost";
import ProtectedRoute from "./utils/ProtectedRoute";
import {useAdminAuth} from "./hooks/useAdminAuth";
import {HelmetProvider} from 'react-helmet-async';
import AdminSendMail from "./pages/admin/AdminSendMail";
import CoursesList from "./pages/admin/courses/CoursesList";
import CourseOverview from "./pages/admin/courses/CourseOverview";
import CourseContent from "./pages/admin/courses/content/CourseContent";
import CourseSections from "./pages/admin/courses/CourseSections";
import AdminCheatsheetList from "./pages/admin/cheatsheet/AdminCheatsheetList";
import AdminCheatsheetMain from "./pages/admin/cheatsheet/AdminCheatsheetMain";
import AdminCheatsheetPage from "./pages/admin/cheatsheet/AdminCheatsheetPage";
import AdminUserPurchases from "./pages/admin/AdminUserPurchases";
import AdminGlossaryList from "./pages/admin/glossary/AdminGlossaryList";
import AdminGlossaryMain from "./pages/admin/glossary/AdminGlossaryMain";
import AdminGlossaryPage from "./pages/admin/glossary/AdminGlossaryPage";
import AdminLanguages from "./pages/admin/AdminLanguages";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminAppUpdates from "./pages/admin/AdminAppUpdates";
import AdminRules from "./pages/admin/AdminRules";
import CourseDetails from "./pages/admin/courses/CourseDetails";
import Rules from "./pages/Rules";
import AdminSendNotification from "./pages/admin/AdminSendNotification";
import {LanguageProvider} from "./context/LanguageProvider";


function AppRoutes() {
    const {isAdmin, loading} = useAdminAuth();

    function LanguageLayout() {
        return (
            <LanguageProvider>
                <Outlet />
            </LanguageProvider>
        );
    }

    return (
        <HelmetProvider>
            <Router>
                <Routes>
                    {/* Redirect root */}
                    <Route
                        path="/"
                        element={<Navigate to="/fa" replace />}
                    />

                    {/* Public site */}
                    <Route path="/:lang" element={<LanguageLayout />}>
                        <Route index element={<Home />} />
                        <Route path="blog" element={<BlogList />} />
                        <Route path="blog/:slug" element={<BlogPost />} />
                        <Route path="rules" element={<Rules />} />
                        <Route path="not-found" element={<NotFound />} />
                    </Route>
                    <Route path="/admin/login" element={<AdminLogin/>}/>
                    <Route
                        path="/admin/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminAnalytics/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog/new-post/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminBlogPost/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/mail/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminSendMail/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/notification/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminSendNotification/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminBlogPosts/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/blog/edit/:id/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminBlogPost/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <CoursesList/>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/:id/overview/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <CourseOverview />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/new/overview/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <CourseOverview />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/:id/details/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <CourseDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/:courseId/content/:contentId/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <CourseContent />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/:courseId/sections/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <CourseSections />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/cheatsheet/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminCheatsheetMain />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/:course_id/cheatsheet/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminCheatsheetList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/:course_id/cheatsheet/:id"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminCheatsheetPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/course/glossary/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminGlossaryMain />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/glossary/:id"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminGlossaryList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/glossary/:course_id/:id"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminGlossaryPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/purchase"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminUserPurchases />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/language"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminLanguages />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/categories"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminCategories />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/updates/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminAppUpdates />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/rules/"
                        element={
                            <ProtectedRoute isAdmin={isAdmin} loading={loading}>
                                <AdminRules />
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </Router>
        </HelmetProvider>
    )
}

export default AppRoutes;
